import { useState, useEffect } from "react";
import {
  X,
  Play,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
} from "lucide-react";
import { OpenAPISpec, ParsedEndpoint } from "../types";
import { resolveRef, generateExampleFromSchema } from "../utils/openapi";
import { cn } from "../utils/cn";
import { buildApiUrl } from "../config/api";
import { METHOD_COLORS } from "./Sidebar";

interface TryItOutModalProps {
  spec: OpenAPISpec;
  endpoint: ParsedEndpoint;
  credentials: Record<string, string>;
  onClose: () => void;
}

export function TryItOutModal({
  spec,
  endpoint,
  credentials,
  onClose,
}: TryItOutModalProps) {
  const { method, path, operation } = endpoint;

  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState<string>("");
  const [serverUrl, setServerUrl] = useState<string>(
    spec.servers?.[0]?.url || "",
  );
  const [response, setResponse] = useState<{
    status: number;
    data: any;
    headers: Record<string, string>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHeadersExpanded, setIsHeadersExpanded] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<
    Array<{ key: string; value: string }>
  >([]);

  const parameters = (operation.parameters || []).map((p: any) =>
    p.$ref ? resolveRef(spec, p.$ref) || p : p,
  );

  useEffect(() => {
    const initialParams: Record<string, string> = {};
    parameters.forEach((p: any) => {
      const schema = p.schema || p;
      let exampleVal =
        p.example !== undefined
          ? p.example
          : schema?.example !== undefined
            ? schema.example
            : schema?.default;

      if (exampleVal === undefined && schema) {
        const generated = generateExampleFromSchema(schema, spec);
        if (generated !== "" && generated !== undefined) {
          exampleVal =
            typeof generated === "object"
              ? JSON.stringify(generated)
              : String(generated);
        }
      } else if (typeof exampleVal === "object") {
        exampleVal = JSON.stringify(exampleVal);
      } else if (exampleVal !== undefined) {
        exampleVal = String(exampleVal);
      }

      if (exampleVal !== undefined && exampleVal !== "") {
        initialParams[p.name] = exampleVal;
      }
    });
    setParamValues(initialParams);

    if (operation.requestBody) {
      const resolvedBody = operation.requestBody.$ref
        ? resolveRef(spec, operation.requestBody.$ref) || operation.requestBody
        : operation.requestBody;
      const jsonContent = resolvedBody.content?.["application/json"];
      if (jsonContent) {
        if (jsonContent.example !== undefined) {
          setRequestBody(
            typeof jsonContent.example === "string"
              ? jsonContent.example
              : JSON.stringify(jsonContent.example, null, 2),
          );
        } else if (
          jsonContent.examples &&
          Object.values(jsonContent.examples).length > 0
        ) {
          const firstExample: any = Object.values(jsonContent.examples)[0];
          const exVal = firstExample.value || firstExample;
          setRequestBody(
            typeof exVal === "string" ? exVal : JSON.stringify(exVal, null, 2),
          );
        } else if (jsonContent.schema) {
          const exampleObj = generateExampleFromSchema(
            jsonContent.schema,
            spec,
          );
          if (exampleObj !== undefined && exampleObj !== "") {
            setRequestBody(JSON.stringify(exampleObj, null, 2));
          }
        }
      }
    }
  }, [spec, endpoint]);

  const handleParamChange = (name: string, value: string) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCustomHeader = () => {
    setCustomHeaders([...customHeaders, { key: "", value: "" }]);
  };

  const handleCustomHeaderChange = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const newHeaders = [...customHeaders];
    newHeaders[index][field] = value;
    setCustomHeaders(newHeaders);
  };

  const handleRemoveCustomHeader = (index: number) => {
    const newHeaders = [...customHeaders];
    newHeaders.splice(index, 1);
    setCustomHeaders(newHeaders);
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      let url = serverUrl + path;

      // Replace path parameters
      const pathParams = parameters.filter((p) => p.in === "path");
      for (const p of pathParams) {
        const val = paramValues[p.name] || "";
        url = url.replace(`{${p.name}}`, encodeURIComponent(val));
      }

      // Add query parameters
      const queryParams = parameters.filter((p) => p.in === "query");
      const searchParams = new URLSearchParams();
      for (const p of queryParams) {
        const val = paramValues[p.name];
        if (val) {
          searchParams.append(p.name, val);
        }
      }
      // Add headers
      const headerParams = parameters.filter((p) => p.in === "header");
      const headers: Record<string, string> = {
        Accept: "application/json",
      };
      for (const p of headerParams) {
        const val = paramValues[p.name];
        if (val) {
          headers[p.name] = val;
        }
      }

      // Add custom headers
      for (const ch of customHeaders) {
        if (ch.key.trim() !== "") {
          headers[ch.key.trim()] = ch.value;
        }
      }

      // Inject Security Credentials
      const securitySchemes =
        spec.components?.securitySchemes || spec.securityDefinitions || {};
      const endpointSecurity = operation.security || spec.security || [];

      if (endpointSecurity.length > 0) {
        // Try to find a security requirement that we have credentials for
        for (const req of endpointSecurity) {
          for (const schemeName of Object.keys(req)) {
            const credValue = credentials[schemeName];
            if (credValue) {
              const scheme = securitySchemes[schemeName];
              if (scheme) {
                if (scheme.type === "apiKey") {
                  if (scheme.in === "header") {
                    headers[scheme.name] = credValue;
                  } else if (scheme.in === "query") {
                    searchParams.append(scheme.name, credValue);
                  }
                } else if (scheme.type === "http") {
                  if (scheme.scheme === "bearer") {
                    headers["Authorization"] = `Bearer ${credValue}`;
                  } else if (scheme.scheme === "basic") {
                    headers["Authorization"] = `Basic ${credValue}`;
                  }
                } else if (
                  scheme.type === "oauth2" ||
                  scheme.type === "openIdConnect"
                ) {
                  headers["Authorization"] = `Bearer ${credValue}`;
                }
              }
            }
          }
        }
      }

      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`;
      }

      const options: RequestInit = {
        method,
        headers,
      };
      void options;

      let finalBody = undefined;
      if (method !== "GET" && method !== "HEAD" && requestBody) {
        headers["Content-Type"] = "application/json";
        finalBody = requestBody;
      }

      // Use our backend proxy to bypass CORS
      const proxyRes = await fetch(buildApiUrl("/api/proxy"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url,
          method,
          headers,
          body: finalBody,
        }),
      });

      const proxyData = await proxyRes.json();

      if (!proxyRes.ok) {
        throw new Error(proxyData.error || "Failed to proxy request");
      }

      setResponse({
        status: proxyData.status,
        data: proxyData.data,
        headers: proxyData.headers,
      });
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4 sm:p-6">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-full flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200/60 bg-zinc-50/50">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "text-[11px] font-bold px-2 py-1 rounded-md border uppercase tracking-widest shadow-sm",
                METHOD_COLORS[method] ||
                  "text-zinc-600 bg-zinc-50 border-zinc-200",
              )}
            >
              {method}
            </span>
            <h2 className="text-lg font-mono text-zinc-900 font-medium tracking-tight">
              {path}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-8">
          {/* Left Column: Inputs */}
          <div className="flex-1 space-y-6">
            {/* Server Selection */}
            {spec.servers && spec.servers.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                  Server
                </label>
                <select
                  value={serverUrl}
                  onChange={(e) => setServerUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all font-mono"
                >
                  {spec.servers.map((s, i) => (
                    <option key={i} value={s.url}>
                      {s.url} {s.description ? `(${s.description})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Parameters */}
            {parameters.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-zinc-900 mb-3 uppercase tracking-wider">
                  Parameters
                </label>
                <div className="space-y-3">
                  {parameters.map((p: any, i: number) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-zinc-800 font-mono">
                          {p.name}
                          {p.required && (
                            <span className="ml-2 text-[9px] text-red-500 uppercase tracking-widest font-sans font-bold bg-red-50 px-1.5 py-0.5 rounded">
                              req
                            </span>
                          )}
                        </span>
                        <span className="text-[11px] text-zinc-400 uppercase tracking-wider">
                          {p.in}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder={p.description || ""}
                        value={paramValues[p.name] || ""}
                        onChange={(e) =>
                          handleParamChange(p.name, e.target.value)
                        }
                        className="w-full px-3 py-2 bg-white border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all placeholder:text-zinc-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request Body */}
            {operation.requestBody && (
              <div>
                <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                  Request Body (JSON)
                </label>
                <textarea
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-xl text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-all placeholder:text-zinc-600"
                  placeholder="{\n  // Enter JSON body here\n}"
                />
              </div>
            )}

            {/* Custom Headers */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider">
                  Custom Headers
                </label>
                <button
                  onClick={handleAddCustomHeader}
                  className="flex items-center gap-1 text-[11px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors bg-zinc-100 hover:bg-zinc-200 px-2 py-1 rounded-md"
                >
                  <Plus className="w-3 h-3" />
                  Add Header
                </button>
              </div>
              {customHeaders.length > 0 ? (
                <div className="space-y-2">
                  {customHeaders.map((header, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Header-Name"
                        value={header.key}
                        onChange={(e) =>
                          handleCustomHeaderChange(index, "key", e.target.value)
                        }
                        className="flex-1 px-3 py-2 bg-white border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all placeholder:text-zinc-300 font-mono"
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) =>
                          handleCustomHeaderChange(
                            index,
                            "value",
                            e.target.value,
                          )
                        }
                        className="flex-1 px-3 py-2 bg-white border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all placeholder:text-zinc-300 font-mono"
                      />
                      <button
                        onClick={() => handleRemoveCustomHeader(index)}
                        className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[13px] text-zinc-400 italic">
                  No custom headers added.
                </div>
              )}
            </div>

            <button
              onClick={handleExecute}
              disabled={loading || !serverUrl}
              className="w-full flex items-center justify-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Execute Request
            </button>

            <div className="flex items-start gap-2 text-[11px] text-zinc-400 bg-zinc-50 p-3 rounded-xl border border-zinc-200/60">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Requests are securely routed through a backend proxy to bypass
                browser CORS restrictions.
              </p>
            </div>
          </div>

          {/* Right Column: Response */}
          <div className="flex-1 flex flex-col border border-zinc-200/60 rounded-2xl overflow-hidden bg-zinc-50/30">
            <div className="px-4 py-3 border-b border-zinc-200/60 bg-white flex items-center justify-between">
              <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">
                Response
              </h3>
              {response && (
                <span
                  className={cn(
                    "text-[11px] font-bold px-2 py-0.5 rounded-md border tracking-widest",
                    response.status >= 200 && response.status < 300
                      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : "text-red-700 bg-red-50 border-red-200",
                  )}
                >
                  {response.status}
                </span>
              )}
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              {error ? (
                <div className="text-sm text-red-600 bg-red-50 p-4 rounded-xl border border-red-100 font-mono text-[13px]">
                  {error}
                </div>
              ) : response ? (
                <div className="space-y-4">
                  <div>
                    <button
                      onClick={() => setIsHeadersExpanded(!isHeadersExpanded)}
                      className="flex items-center gap-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2 hover:text-zinc-600 transition-colors"
                    >
                      {isHeadersExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                      Headers
                    </button>
                    {isHeadersExpanded && (
                      <pre className="bg-white border border-zinc-200/60 p-3 rounded-xl text-[11px] font-mono text-zinc-600 overflow-x-auto">
                        {Object.entries(response.headers)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join("\n")}
                      </pre>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                      Body
                    </div>
                    <pre className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl text-[13px] font-mono text-zinc-100 overflow-x-auto">
                      {typeof response.data === "object"
                        ? JSON.stringify(response.data, null, 2)
                        : response.data}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-400 text-sm">
                  Click execute to see the response
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
